import { FC, useCallback, useMemo, useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import { useGetUsersFilteredQuery } from '@api/user';
import { EmptyResultResponse, PieceAction } from '@apiContract';
import { ActionComponentProps } from '@components/MailpieceActions';
import { DialogIdCardConfirmation } from '@components/MailpieceActions/components/DialogIdCardConfirmation';
import { AxiosResponse } from 'axios';

const FinishPickUpWithIDCard: FC<ActionComponentProps> = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  pendingSearchValue,
}) => {
  const [updatePieces] = useUpdatePiecesMutation();
  const [actionInProgress, setActionInProgress] = useState(false);

  const recipientId = mailpieces[0]?.recipient?.id;

  const { data: usersData } = useGetUsersFilteredQuery(
    { Ids: recipientId ? [recipientId] : [] },
    { skip: !recipientId || !actionStarted },
  );

  const recipientFull = usersData?.items?.[0];

  const isSearchMatching = useMemo(() => {
    const search = pendingSearchValue?.trim();
    if (!search) return false;

    const recipientUserName = mailpieces[0]?.recipient?.userName;
    const isRecipientMatch = recipientUserName === search;

    const assistants = recipientFull?.assistants || [];
    const isAssistantMatch = assistants.some((assistant) => assistant.userName === search);

    return isRecipientMatch || isAssistantMatch;
  }, [pendingSearchValue, mailpieces, recipientFull]);

  const handleIdEntered = useCallback(
    async (idData: string, onError: (error: string) => void) => {
      setActionInProgress(true);

      try {
        await updatePieces({
          pieces: mailpieces.map((piece) => ({ id: piece.id })),
          action: PieceAction.FinishPickupWithIdCard,
          pickUpIdCard: idData,
        }).unwrap();

        onActionFinish();
      } catch (error) {
        const errorMessage =
          (error as AxiosResponse<EmptyResultResponse>)?.data?.userMessage || 'Invalid ID card value';
        onError(errorMessage);
      } finally {
        setActionInProgress(false);
      }
    },
    [mailpieces, updatePieces, onActionFinish],
  );

  return (
    <DialogIdCardConfirmation
      mailpieces={mailpieces}
      actionStarted={actionStarted}
      actionRequestInProgress={actionInProgress}
      onActionFinish={onActionFinish}
      onIdEntered={handleIdEntered}
      isSearchMatching={isSearchMatching}
      pendingSearchValue={pendingSearchValue}
    />
  );
};

export default FinishPickUpWithIDCard;
